/**
 * Track which page section the visitor is viewing (experiment).
 *
 * 1. Match live headings / eyebrow pills to page-voice sections.
 * 2. Pick the section the visitor has entered (anchor at/above focus line).
 * 3. Fall back to document scroll progress → band index.
 *
 * Debounced so pills don't thrash on every pixel.
 */

import {
  normalizeHeadingKey,
  sectionIndexFromAnchors,
  sectionIndexFromProgress,
  type PageSection,
} from "./section-voice.ts";

export type SectionScrollHandle = {
  /** Force recompute (after SPA nav / new sections). */
  refresh: () => void;
  stop: () => void;
  getIndex: () => number;
};

export type SectionScrollOptions = {
  sections: readonly PageSection[];
  onChange: (index: number) => void;
  /** Settle delay before firing onChange (ms). */
  settleMs?: number;
};

/**
 * Collect candidate heading elements (skip nav/footer chrome when obvious).
 *
 * Prefer h1–h3 + explicit `[data-bhai-section]` markers. When `sectionKeys`
 * is provided, also pick short leaf labels (p/span/div/button/…) whose
 * normalized text matches a page-voice section — marketing sites often
 * render "THE DASHBOARD" as a pill, not an H2.
 */
export function collectHeadingElements(
  root: ParentNode = document,
  sectionKeys?: readonly string[],
): HTMLElement[] {
  const nodes = root.querySelectorAll<HTMLElement>(
    "h1, h2, h3, h4, [data-bhai-section], [role='heading']",
  );
  const out: HTMLElement[] = [];
  const seen = new Set<HTMLElement>();
  const push = (el: HTMLElement) => {
    if (seen.has(el)) return;
    const text = (el.innerText || el.textContent || "").replace(/\s+/g, " ").trim();
    if (text.length < 2 || text.length > 120) return;
    if (el.closest("nav, header, footer, [role='navigation'], [role='banner']")) return;
    // Skip hidden / zero-size chrome.
    try {
      const r = el.getBoundingClientRect();
      if (r.width < 2 && r.height < 2) return;
    } catch {
      /* ignore */
    }
    const key = normalizeHeadingKey(text);
    if (
      /^(bluehatai|bluehat|blue hat(?: ai)?|powered by(?: bluehatai| bluehat)?)$/i.test(
        key,
      )
    ) {
      return;
    }
    seen.add(el);
    out.push(el);
  };

  for (const el of nodes) {
    if (el instanceof HTMLElement) push(el);
  }

  // Section-label text match (pills / eyebrows that are not real headings).
  if (sectionKeys && sectionKeys.length > 0) {
    const want = new Set(
      sectionKeys.map((k) => normalizeHeadingKey(k)).filter((k) => k.length >= 3),
    );
    if (want.size > 0) {
      // div/button/a: CI page uses pill badges, not always <p>/<span>.
      const candidates = root.querySelectorAll<HTMLElement>(
        "p, span, div, a, button, label, li, strong, em",
      );
      for (const el of candidates) {
        if (!(el instanceof HTMLElement)) continue;
        // Prefer leaf-ish labels (avoid large wrapping blocks).
        if (
          el.querySelector(
            "p, span, h1, h2, h3, h4, a, button, ul, ol, img, div, svg, input",
          )
        ) {
          continue;
        }
        const text = (el.innerText || el.textContent || "").replace(/\s+/g, " ").trim();
        if (text.length < 3 || text.length > 60) continue;
        const key = normalizeHeadingKey(text);
        if (want.has(key)) push(el);
      }
    }
  }

  // Document order (querySelectorAll is already ordered; merge of two sets may not be).
  out.sort((a, b) => {
    const pos = a.compareDocumentPosition(b);
    if (pos & Node.DOCUMENT_POSITION_FOLLOWING) return -1;
    if (pos & Node.DOCUMENT_POSITION_PRECEDING) return 1;
    return 0;
  });
  return out;
}

export function headingKeyForElement(el: HTMLElement): string {
  const text = (el.innerText || el.textContent || "").replace(/\s+/g, " ").trim();
  return normalizeHeadingKey(text);
}

/** Document scroll progress 0..1 (viewport reading-line bias). */
export function documentScrollProgress(): number {
  const doc = document.documentElement;
  const scrollTop = window.scrollY || doc.scrollTop || 0;
  const viewH = window.innerHeight || 1;
  const full = Math.max(1, (doc.scrollHeight || 1) - viewH);
  const mid = scrollTop + viewH * 0.4;
  return Math.max(0, Math.min(1, mid / (full + viewH * 0.4 || 1)));
}

/**
 * Start observing. Calls onChange when the active section index settles.
 */
export function startSectionScroll(opts: SectionScrollOptions): SectionScrollHandle {
  let index = -1;
  let stopped = false;
  let settleTimer: ReturnType<typeof setTimeout> | null = null;
  /** Longer settle = less thrash between neighboring sections while reading. */
  const settleMs = opts.settleMs ?? 340;
  /** Candidate must stay stable for the full settle window before commit. */
  let pending: number | null = null;

  const emit = (next: number) => {
    if (stopped) return;
    if (opts.sections.length === 0) return;
    const n = Math.max(0, Math.min(opts.sections.length - 1, next));
    if (n === index) return;
    index = n;
    opts.onChange(index);
  };

  const recompute = () => {
    if (stopped || opts.sections.length === 0) return;

    const sectionKeys = opts.sections.map((s) => s.heading);
    const headings = collectHeadingElements(document, sectionKeys);
    const vh = window.innerHeight || 1;
    // Slightly above mid-viewport — “what am I reading now?”
    const focusY = vh * 0.36;

    const anchors = headings.map((el) => ({
      key: headingKeyForElement(el),
      top: el.getBoundingClientRect().top,
    }));

    const fromDom = sectionIndexFromAnchors(opts.sections, anchors, focusY);
    if (fromDom != null) {
      schedule(fromDom);
      return;
    }
    // No live heading matched this page's sections → pure scroll bands.
    schedule(sectionIndexFromProgress(documentScrollProgress(), opts.sections.length));
  };

  const schedule = (next: number) => {
    // Same as current → cancel pending switch.
    if (next === index) {
      pending = null;
      if (settleTimer) {
        clearTimeout(settleTimer);
        settleTimer = null;
      }
      return;
    }
    // New candidate — (re)start settle clock only when it changes.
    if (pending === next && settleTimer) return;
    pending = next;
    if (settleTimer) clearTimeout(settleTimer);
    settleTimer = setTimeout(() => {
      const commit = pending;
      pending = null;
      settleTimer = null;
      if (commit != null) emit(commit);
    }, settleMs);
  };

  const onScroll = () => recompute();
  window.addEventListener("scroll", onScroll, { passive: true });
  window.addEventListener("resize", onScroll, { passive: true });

  if (opts.sections.length > 0) {
    emit(0);
  }
  recompute();

  return {
    refresh: () => recompute(),
    stop: () => {
      stopped = true;
      if (settleTimer) clearTimeout(settleTimer);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    },
    getIndex: () => (index < 0 ? 0 : index),
  };
}
